import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

interface UseConsumeQueryParamReturn {
  value: string | null;
  consume: () => void;
}

/**
 * Reads a deep-link query param and returns a `consume()` function that
 * removes it from the URL. The URL is the source of truth, so a fresh value
 * arriving later in the same mount (e.g. a second in-app navigation) will
 * produce a fresh `value` and re-trigger the consumer.
 *
 * Multiple call sites use this independently for different params without
 * interfering, since the only shared state is the URL.
 */
export function useConsumeQueryParam(
  paramName: string
): UseConsumeQueryParamReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get(paramName);

  const consume = useCallback(() => {
    setSearchParams(
      (prev) => {
        prev.delete(paramName);
        return prev;
      },
      { replace: true }
    );
  }, [paramName, setSearchParams]);

  return { value, consume };
}
